import { useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { IconInfoSmall } from '@components/icons/info-small';
import { SelectMulti } from '@components/selectmulti';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { api } from 'api';
import { SubmitError } from 'api/departments/requests';
import { ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';
import { IUser } from 'models/types';

type Props = ModalProps<{
  departmentId: number;
  departmentTitle: string;
  departmentUserIds: number[];
  canEditFullAccess: boolean;
}>;

export const AddDepartmentUserModal = (props: Props) => {
  const { t, userStore } = useStore();
  const { closeModal, departmentTitle, departmentId, departmentUserIds, canEditFullAccess } = props;
  const { showError, showSuccess } = useToaster();

  const users = useMemo(() => {
    return userStore.list
      .filter((user: IUser) => !departmentUserIds.includes(user.id))
      .map((user: IUser) => ({ value: user.id, label: user.name }));
  }, [userStore.list, departmentUserIds]);

  const [userIds, setUserIds] = useState<string[]>([]);
  const [fullAccess, setFullAccess] = useState(false);

  const queryClient = useQueryClient();
  const submit = useMutation<{}, SubmitError>(
    () => api.departments.addUsers(departmentId, userIds, fullAccess),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['departments']);
        userStore.load(true); // To load the user's updated departments
        showSuccess(t('modals.adddepartmentuser.success'));
        closeModal();
      },
      onError: (error) => {
        showError(error.response ? error.response.data.error : error.message);
      },
    },
  );

  return (
    <Stack space={6}>
      <div>
        <Text font="heading1" text={t('modals.adddepartmentuser.header')} />
        <Text font="regular16" text={t('modals.adddepartmentuser.subheader')} />
      </div>
      <Stack space={4}>
        <Stack space={0}>
          <Text font="semibold14" text={t('modals.adddepartmentuser.department.label')} />
          <Text font="regular16" text={departmentTitle} />
        </Stack>
        <SelectMulti
          label={t('modals.adddepartmentuser.users.label')}
          options={users}
          onChange={(options) => setUserIds(options.map((option) => option.value))}
          noOptions={() => t('modals.adddepartmentuser.user.nooptions')}
          placeholder={t('common.select')}
        />
        <Stack space={0}>
          <Stack space={1} orientation="horizontal" align="center">
            <Text font="semibold14" text={t('modals.adddepartmentuser.fullaccess.label')} />
            <IconInfoSmall
              color="purple50"
              tooltip={t('modals.adddepartmentuser.fullaccess.tooltip')}
            />
          </Stack>
          <Stack space={0} orientation="horizontal" justify={'space-between'}>
            <Text text={fullAccess ? t('common.yes') : t('common.no')} />
            <Switch
              onChange={setFullAccess}
              label={t('modals.adddepartmentuser.fullaccess.label')}
              labelVisible={false}
              defaultValue={fullAccess}
              isDisabled={!canEditFullAccess}
            />
          </Stack>
        </Stack>
        <Text font="regular14" text={t('modals.adddepartmentuser.fullaccess.description')} />
        {fullAccess && (
          <Text
            color="red100"
            font="regular14"
            text={t('modals.adddepartmentusers.fullaccess.yousure')}
          />
        )}
      </Stack>
      <Stack orientation="horizontal" space={4} justify="flex-end">
        <Button
          variant="secondary"
          onClick={closeModal}
          text={t('modals.adddepartmentuser.cancel')}
        />
        <Button
          isDisabled={!userIds.length || submit.isLoading}
          onClick={() => submit.mutate()}
          variant="primary-blue"
          text={t('modals.adddepartmentuser.save')}
        />
      </Stack>
    </Stack>
  );
};
